import { Link } from "wouter";
import { Users, MessageSquare, UserPlus, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LazySection } from "@/components/LazySection";
import { ActivityFeed } from "@/components/ActivityFeed";
import type { User as UserType } from "@shared/schema";

interface NetworkingTabProps {
  user: UserType;
  dashboardStats: any;
  connections: any[];
  connectionsLoading: boolean;
}

function ConnectionCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center">
          <Skeleton className="h-16 w-16 rounded-full mb-3" />
          <Skeleton className="h-5 w-32 mb-1" />
          <Skeleton className="h-4 w-24 mb-1" />
          <Skeleton className="h-4 w-28 mb-3" />
          <Skeleton className="h-9 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

function getInitials(name?: string | null) {
  if (!name) return "U";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

export function NetworkingTab({ user, dashboardStats, connections, connectionsLoading }: NetworkingTabProps) {
  const acceptedConnections = (connections || []).filter((c: any) => c.status === "accepted");

  return (
    <div className="space-y-8">
      <section data-testid="section-net-connections">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Your Network</h2>
            <p className="text-muted-foreground">Your connections and colleagues</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" asChild data-testid="button-explore-network-tab">
              <Link href="/network">
                Explore
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild data-testid="button-view-messages">
              <Link href="/messages">
                <MessageSquare className="mr-2 h-4 w-4" />
                Messages
              </Link>
            </Button>
          </div>
        </div>

        {connectionsLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => <ConnectionCardSkeleton key={i} />)}
          </div>
        ) : acceptedConnections.length === 0 ? (
          <Card data-testid="empty-connections">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No connections yet</p>
                <p className="text-sm text-muted-foreground mt-1">Browse the directory to find people to connect with.</p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link href="/directory" data-testid="link-browse-directory">Browse Directory</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {acceptedConnections.slice(0, 8).map((connection: any) => {
              const other = connection.connectedUser;
              if (!other) return null;
              const fullName = `${other.firstName || ""} ${other.lastName || ""}`.trim();
              return (
                <Card key={connection.id} className="hover-elevate" data-testid={`card-connection-${connection.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <Avatar className="h-16 w-16 mb-3" data-testid={`avatar-connection-${connection.id}`}>
                        <AvatarImage src={other.profileImageUrl || undefined} alt={fullName} />
                        <AvatarFallback>{getInitials(fullName)}</AvatarFallback>
                      </Avatar>
                      <h3 className="font-semibold text-foreground mb-1" data-testid={`text-connection-name-${connection.id}`}>
                        {fullName || "Unknown"}
                      </h3>
                      {other.title && (
                        <p className="text-sm text-muted-foreground">{other.title}</p>
                      )}
                      {other.company && (
                        <p className="text-sm text-muted-foreground mb-2">{other.company}</p>
                      )}
                      <Button size="sm" variant="secondary" asChild data-testid={`button-message-${connection.id}`}>
                        <Link href={`/messages?to=${other.id}`}>Message</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <LazySection
        className=""
        minHeight="300px"
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      >
        <section data-testid="section-net-activity">
          <ActivityFeed />
        </section>
      </LazySection>
    </div>
  );
}
